// frontend/src/components/Dashboard/ArticleDetailModal.js
import React, { useState } from 'react';
import { 
    XMarkIcon, 
    SparklesIcon, 
    ArrowTopRightOnSquareIcon,
    TagIcon,
    UserGroupIcon,
    BuildingOfficeIcon,
    UserIcon as PersonIcon, // Renamed to avoid conflict if UserIcon is used elsewhere
    CpuChipIcon // Example for products/models
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../Common/LoadingSpinner';
import AIAnalysisModal from '../Common/AIAnalysisModal';
import { copyArticleForAnalysis } from '../../utils/geminiIntegration';

// Helper component for displaying a list of tags under a heading
const InfoSection = ({ title, items, itemColorClass = "bg-gray-100 text-gray-800", icon: Icon }) => {
    if (!items || items.length === 0) {
        // Optionally render nothing or a placeholder
        // return <p className="text-xs text-gray-500">{title}: Not available</p>;
        return null;
    }
    return (
        <div className="mt-3">
            <h4 className="text-xs font-semibold text-gray-500 mb-1.5 flex items-center">
                {Icon && <Icon className="h-4 w-4 mr-1.5 text-gray-400" />}
                {title}:
            </h4>
            <div className="flex flex-wrap gap-2">
                {items.map((item, index) => (
                    <span key={index} className={`inline-block ${itemColorClass} text-xs font-semibold px-2.5 py-1 rounded-full`}>
                        {item}
                    </span>
                ))}
            </div>
        </div>
    );
};

// Helper component for displaying key entities
const KeyEntitiesSection = ({ entities }) => {
    if (!entities || Object.keys(entities).length === 0 || 
        (entities.organizations?.length === 0 && entities.products_models?.length === 0 && entities.people?.length === 0) ) {
        return null;
    }

    return (
        <div className="mt-4 pt-4 border-t border-gray-100">
            <h4 className="text-sm font-semibold text-gray-600 mb-2">Key Entities Mentioned:</h4>
            {entities.organizations && entities.organizations.length > 0 && (
                <InfoSection title="Organizations" items={entities.organizations} icon={BuildingOfficeIcon} itemColorClass="bg-orange-100 text-orange-700" />
            )}
            {entities.products_models && entities.products_models.length > 0 && (
                <InfoSection title="Products/Models" items={entities.products_models} icon={CpuChipIcon} itemColorClass="bg-cyan-100 text-cyan-700" />
            )}
            {entities.people && entities.people.length > 0 && (
                <InfoSection title="People" items={entities.people} icon={PersonIcon} itemColorClass="bg-lime-100 text-lime-700" />
            )}
        </div>
    );
};


const ArticleDetailModal = ({ article, onClose, url: articleUrlProp }) => {
  const [showAIModal, setShowAIModal] = useState(false);

  if (!article) return null;

  const {
    title,
    source_name,
    publication_date,
    classification,
    content_preview,
  } = article;

  const displayUrl = articleUrlProp || article.url;

  const summaryText = classification?.summary || content_preview || "Details not available.";
  const topics = classification?.topics || [];
  const technicalScore = classification?.technical_score;
  // New classification fields
  const contentTypes = classification?.content_types || [];
  const keyEntities = classification?.key_entities || {}; // Default to empty object
  const targetAudiences = classification?.target_audiences || [];


  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC'
    });
  };

  const handleGeminiAnalysis = () => {
    setShowAIModal(true);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4 transition-opacity duration-300 ease-in-out">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full flex flex-col max-h-[90vh] transform transition-all duration-300 ease-in-out">
        <div className="sticky top-0 bg-white p-6 sm:p-8 border-b border-gray-200 z-10">
          <div className="flex justify-between items-start">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 flex-1 mr-4">{title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close modal"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="flex-grow overflow-y-auto p-6 sm:p-8 space-y-4 text-sm text-gray-700">
          <div className="flex items-center text-xs text-gray-500">
            <span className="font-medium">{source_name || 'Unknown Source'}</span>
            <span className="mx-2">&bull;</span>
            <span>{formatDate(publication_date)}</span>
          </div>

          <h4 className="text-md font-semibold text-gray-700 mt-2 mb-1">Summary</h4>
          <p className="leading-relaxed whitespace-pre-wrap">{summaryText}</p>

          {technicalScore !== null && technicalScore !== undefined && (
            <div className="mt-3">
              <span className="text-xs font-semibold text-gray-500 mr-1">Tech Level:</span>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                technicalScore >= 4 ? 'bg-red-100 text-red-700' :
                technicalScore >= 3 ? 'bg-yellow-100 text-yellow-700' :
                'bg-green-100 text-green-700'
              }`}>
                {technicalScore}/5
              </span>
            </div>
          )}

          {/* Display Topics */}
          <InfoSection title="Topics" items={topics} icon={TagIcon} itemColorClass="bg-blue-100 text-blue-700" />
          
          {/* Display Content Types */}
          <InfoSection title="Content Types" items={contentTypes} icon={TagIcon} itemColorClass="bg-purple-100 text-purple-700" />

          {/* Display Target Audiences */}
          <InfoSection title="Target Audiences" items={targetAudiences} icon={UserGroupIcon} itemColorClass="bg-teal-100 text-teal-700" />
          
          {/* Display Key Entities */}
          <KeyEntitiesSection entities={keyEntities} />

        </div>

        <div className="sticky bottom-0 bg-white p-6 border-t border-gray-200 z-10">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <button
                onClick={handleGeminiAnalysis}
                className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
                aria-label="Analyze with Gemini AI"
              >
                <SparklesIcon className="h-5 w-5 mr-2" />
                Analyze with Gemini
              </button>
            </div>
            <a
              href={displayUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-4 rounded-lg text-sm transition duration-150 ease-in-out"
            >
              <ArrowTopRightOnSquareIcon className="h-5 w-5 mr-2" />
              Read Full Article
            </a>
          </div>
        </div>
      </div>
      
      {showAIModal && (
        <AIAnalysisModal 
          isOpen={showAIModal}
          onClose={() => setShowAIModal(false)}
          article={article}
          onAnalyze={copyArticleForAnalysis}
        />
      )}
    </div>
  );
};

export default ArticleDetailModal;
